/**
 * useShapeDrag Hook
 *
 * Thin wrapper for shape-specific whole-geometry drag functionality.
 * Delegates to generic `useGeometryWholeDrag` hook with shape configuration.
 *
 * Features:
 * - Drag entire shape geometry (not individual vertices/edges)
 * - Works with MapLibre GL JS feature queries
 * - Security: Only works when drag mode active + correct task
 * - Prevents conflicts with resize handles (vertex/edge drag)
 * - Automatic history integration via Zustand
 * - Auto-pan support when dragging near edges
 *
 * @module hooks/useShapeDrag
 */

import { useMemo } from 'react';
import { useGeometryWholeDrag } from './drag/useGeometryWholeDrag';
import { createShapeDragConfig } from './drag/configs/shapeDragConfig';

/**
 * Custom hook for shape drag functionality
 *
 * Supports whole-shape dragging using MapLibre queryRenderedFeatures.
 *
 * Drag is enabled when ALL conditions are met:
 * 1. Drawing mode is active (isDrawing = true)
 * 2. Task form is open (create/edit)
 * 3. This geometry belongs to the currently selected task (or is preview)
 * 4. Active tool is not Selection mode
 *
 * Automatically skips drag when clicking on resize handles to avoid conflicts.
 *
 * @param geometryId - The ID of the shape geometry being dragged
 * @param taskId - The ID of the task this geometry belongs to
 * @returns Object containing drag state
 */
export const useShapeDrag = (geometryId: string, taskId: string) => {
  // Create shape-specific configuration (memoized to prevent unnecessary re-renders)
  const config = useMemo(() => createShapeDragConfig(), []);

  // Delegate to generic drag hook with shape config
  return useGeometryWholeDrag(geometryId, taskId, config);
};
